import { useMutation } from '@apollo/client';
import { isAfter, parseISO } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
	Button,
	Grid,
	GridCol,
	Heading,
	Input,
	Popup,
	PopupSection,
	Select,
	Spinner,
	Text,
	Textarea,
} from '@flixbus/honeycomb-react';

import { AUXILIARY_WA_STATUS } from 'common/ActivityChip/const';
import { WA_AUXILIARY_SUCCESS_RESPONSE } from 'constants/activityTypes';
import { standardError, standardSuccess } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';
import { combineDateTime } from 'pages/WorkAssignments/components/CreateBreakdownFlow/utils/incidentTime';
import {
	getMaxAllowedPastDate,
	isTodayOrFutureInputDate,
} from 'pages/WorkAssignments/components/PopUpEditAssignment/helpers';
import { formatHoursMinutesTime, getDateToEdit } from 'utils/datetime';

import { UPDATE_AUXILIARY_WORK_ASSIGNMENT } from './qql/updateAuxiliaryWorkAssignment';
import { MANUAL_RECONCILIATION_TEXT as modalManualReconciliationText } from './text';

import styles from './styles.module.css';

const ModalManualReconciliation = ({
	initialData = {},
	baseTimezone,
	onActionSuccess = () => {},
	onClose,
}) => {
	const timezone = initialData.timezone || baseTimezone;

	const defaultValues = useMemo(() => {
		const { fromTimestamp, toTimestamp } = initialData;

		return {
			departureDate: fromTimestamp ? getDateToEdit(fromTimestamp, timezone) : '',
			departureTime: fromTimestamp ? formatHoursMinutesTime(fromTimestamp, timezone) : '',
			arrivalDate: toTimestamp ? getDateToEdit(toTimestamp, timezone) : '',
			arrivalTime: toTimestamp ? formatHoursMinutesTime(toTimestamp, timezone) : '',
			note: '',
		};
	}, [initialData, timezone]);

	const {
		control,
		formState: { isValid },
		handleSubmit,
		getValues,
		trigger,
		watch,
	} = useForm({ mode: 'onChange', defaultValues });

	const maxAllowedPastDate = useMemo(() => getMaxAllowedPastDate(), []);
	const departureDate = watch('departureDate');

	const departureDateRules = useMemo(
		() => ({
			required: true,
			validate: (value) =>
				!isTodayOrFutureInputDate(value) || modalManualReconciliationText.pastDateError,
		}),
		[]
	);

	const arrivalDateRules = useMemo(
		() => ({
			required: true,
			validate: (value) =>
				!isTodayOrFutureInputDate(value) || modalManualReconciliationText.pastDateError,
		}),
		[]
	);

	const arrivalTimeRules = useMemo(
		() => ({
			required: true,
			validate: (value) => {
				const vals = getValues();
				if (!vals.departureDate || !vals.departureTime || !vals.arrivalDate || !value) return true;

				const departure = parseISO(`${vals.departureDate}T${vals.departureTime}`);
				const arrival = parseISO(`${vals.arrivalDate}T${value}`);
				return (
					isAfter(arrival, departure) || modalManualReconciliationText.arrivalAfterDepartureError
				);
			},
		}),
		[getValues]
	);

	const [updateAuxiliaryWA, { loading }] = useMutation(UPDATE_AUXILIARY_WORK_ASSIGNMENT, {
		onCompleted: (data) => {
			const response = data?.updateAuxiliaryWorkAssignment;

			if (response?.__typename === WA_AUXILIARY_SUCCESS_RESPONSE) {
				notificationStore.setNotification(standardSuccess);
				onActionSuccess(response?.workAssignment?.fromTimestamp);
				onClose();
				return;
			}

			notificationStore.setNotification(standardError);
		},
		onError: () => {
			notificationStore.setNotification(standardError);
		},
	});

	const onSubmit = useCallback(
		(formData) => {
			updateAuxiliaryWA({
				variables: {
					input: {
						workAssignmentKey: {
							id: initialData.workAssignmentId,
							version: Number(initialData.workAssignmentVersion),
						},
						status: AUXILIARY_WA_STATUS.actual,
						reason: formData.note || '',
						times: {
							fromTimestamp: combineDateTime(
								formData.departureDate,
								formData.departureTime,
								timezone
							),
							toTimestamp: combineDateTime(formData.arrivalDate, formData.arrivalTime, timezone),
						},
					},
				},
			});
		},
		[updateAuxiliaryWA, initialData, timezone]
	);

	return (
		<Popup
			active
			id="popup-manual-reconciliation"
			aria-haspopup="dialog"
			extraClasses={styles.popup}
			onOverlayClick={onClose}
		>
			<PopupSection type="title" extraClasses={styles.marginBottom}>
				<Heading flushSpace Elem="h5" size={2}>
					{modalManualReconciliationText.title}
				</Heading>
				<Text>{modalManualReconciliationText.description}</Text>
			</PopupSection>

			{!loading && (
				<PopupSection type="content">
					<form
						id="manual-reconciliation-form"
						data-testid="manual-reconciliation-form"
						onSubmit={handleSubmit(onSubmit)}
					>
						<Grid applyContainer justify="left">
							<GridCol size={6}>
								<Grid applyContainer justify="space-between">
									<GridCol size={12}>
										<Heading flushSpace Elem="h5" size={3} extraClasses={styles.textAlign}>
											{modalManualReconciliationText.actualDeparture}
										</Heading>
									</GridCol>

									<GridCol size={6} extraClasses={styles.marginTop}>
										<Controller
											name="departureDate"
											control={control}
											rules={departureDateRules}
											render={({ field: { ref: _ref, onChange, ...field }, fieldState }) => (
												<Input
													id="mr-departureDate"
													type="date"
													label={modalManualReconciliationText.dateLabel}
													max={maxAllowedPastDate}
													valid={!fieldState.invalid}
													infoError={fieldState.error?.message}
													{...field}
													onChange={(e) => {
														onChange(e);
														trigger('arrivalTime');
													}}
												/>
											)}
										/>
									</GridCol>

									<GridCol size={6} extraClasses={styles.marginTop}>
										<Controller
											name="departureTime"
											control={control}
											rules={{ required: true }}
											render={({ field: { ref: _ref, onChange, ...field } }) => (
												<Input
													id="mr-departureTime"
													type="time"
													label={modalManualReconciliationText.timeLabel}
													{...field}
													onChange={(e) => {
														onChange(e);
														trigger('arrivalTime');
													}}
												/>
											)}
										/>
									</GridCol>
								</Grid>
							</GridCol>

							<GridCol size={6}>
								<Grid applyContainer justify="left">
									<GridCol size={12}>
										<Heading flushSpace Elem="h5" size={3} extraClasses={styles.textAlign}>
											{modalManualReconciliationText.actualArrival}
										</Heading>
									</GridCol>

									<GridCol size={6} extraClasses={styles.marginTop}>
										<Controller
											name="arrivalDate"
											control={control}
											rules={arrivalDateRules}
											render={({ field: { ref: _ref, onChange, ...field }, fieldState }) => (
												<Input
													id="mr-arrivalDate"
													type="date"
													label={modalManualReconciliationText.dateLabel}
													min={departureDate}
													max={maxAllowedPastDate}
													valid={!fieldState.invalid}
													infoError={fieldState.error?.message}
													{...field}
													onChange={(e) => {
														onChange(e);
														trigger('arrivalTime');
													}}
												/>
											)}
										/>
									</GridCol>

									<GridCol size={6} extraClasses={styles.marginTop}>
										<Controller
											name="arrivalTime"
											control={control}
											rules={arrivalTimeRules}
											render={({ field: { ref: _ref, ...field }, fieldState }) => (
												<Input
													id="mr-arrivalTime"
													type="time"
													label={modalManualReconciliationText.timeLabel}
													valid={!fieldState.invalid}
													infoError={fieldState.error?.message}
													{...field}
												/>
											)}
										/>
									</GridCol>
								</Grid>
							</GridCol>

							<GridCol size={6} extraClasses={styles.marginTop}>
								<Select
									id="mr-fromLocation"
									label={modalManualReconciliationText.fromLocationLabel}
									disabled
									value={initialData.fromStationName || ''}
								>
									<option>{initialData.fromStationName || ''}</option>
								</Select>
							</GridCol>

							<GridCol size={6} extraClasses={styles.marginTop}>
								<Select
									id="mr-toLocation"
									label={modalManualReconciliationText.toLocationLabel}
									disabled
									value={initialData.toStationName || ''}
								>
									<option>{initialData.toStationName || ''}</option>
								</Select>
							</GridCol>

							<GridCol size={12} extraClasses={styles.marginTop}>
								<Controller
									name="note"
									control={control}
									render={({ field: { ref: _ref, ...field } }) => (
										<Textarea
											id="mr-note"
											label={modalManualReconciliationText.noteLabel}
											{...field}
										/>
									)}
								/>
							</GridCol>
						</Grid>
					</form>
				</PopupSection>
			)}

			{loading && (
				<PopupSection type="icon">
					<Spinner />
				</PopupSection>
			)}

			<PopupSection type="actions" extraClasses={styles.marginTop}>
				<Button onClick={onClose}>{modalManualReconciliationText.cancelBtn}</Button>
				<Button
					type="submit"
					appearance="primary"
					form="manual-reconciliation-form"
					disabled={loading || !isValid}
				>
					{modalManualReconciliationText.confirmBtn}
				</Button>
			</PopupSection>
		</Popup>
	);
};

export default ModalManualReconciliation;
